import { EmptyState } from '../Kit/_index';
import { onBuildPoints, onBuildTicks, onFormatShort, onResolveMax } from './_chartScale';

const BLOCK = 'LStudio__app_chart';

const PAD_TOP = 8;
const VIEW_W = 600;
const VIEW_H = 220;

/*
    🔥 GRÁFICO DE LÍNEAS

    series: [{ label: 'Ventas', tone: 'accent', values: [12, 40, 33, …] }, …]
    labels: ['Ene', 'Feb', …]

    ⚠️ TODAS LAS SERIES COMPARTEN LA MISMA ESCALA

    Si cada línea tuviera su propio máximo, dos series de magnitudes
    distintas se verían igual de altas y la comparación sería mentira.
    El máximo sale del valor más alto de TODAS.

    ⚠️ vector-effect EN EL TRAZO

    El SVG se estira con preserveAspectRatio="none" para ocupar el
    ancho disponible. Sin non-scaling-stroke, ese estirado deforma el
    grosor de la línea: gruesa en horizontal, fina en vertical.
*/

const onToPath = (points) => points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

export const ChartLine = ({
    series = [], labels = [], max = null,
    height = 220, showGrid = true, showArea = true,
    showDots = true, showLegend = true, formatValue = onFormatShort,
    emptyLabel = 'Sin datos para graficar', ariaLabel = 'Gráfico de líneas',
}) => {

    const HAS_DATA = series.some((s) => (s.values ?? []).length);

    if (!HAS_DATA) return <EmptyState title={emptyLabel} size="sm" />;

    const ALL_VALUES = series.flatMap((s) => (s.values ?? []).map((v) => Number(v) || 0));

    const MAX = onResolveMax(ALL_VALUES, max);

    const TICKS = onBuildTicks(MAX, 4);

    return (
        <div className={BLOCK} style={{ '--LS__chart_h': `${height}px` }}>

            {
                showLegend && series.length > 1 && (
                    <div className={`${BLOCK}__legend`}>
                        {
                            series.map((s, i) => (
                                <span key={s.label ?? i} className={`${BLOCK}__legend_item`}
                                    data-tone={s.tone ?? 'accent'} >
                                    <i className={`${BLOCK}__legend_dot`} aria-hidden="true" />
                                    {s.label}
                                </span>
                            ))
                        }
                    </div>
                )
            }

            <div className={`${BLOCK}__plot`}>

                {
                    showGrid && (
                        <div className={`${BLOCK}__axis`} aria-hidden="true">
                            {
                                [...TICKS].reverse().map((tick) => (
                                    <span key={tick} className={`${BLOCK}__axis_tick`}>
                                        {formatValue(tick)}
                                    </span>
                                ))
                            }
                        </div>
                    )
                }

                <svg className={`${BLOCK}__svg`} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                    preserveAspectRatio="none" role="img" aria-label={ariaLabel} >

                    {
                        showGrid && TICKS.map((tick) => {
                            const Y = VIEW_H - (tick / MAX) * (VIEW_H - PAD_TOP);
                            return (
                                <line key={tick} className={`${BLOCK}__grid`}
                                    x1="0" x2={VIEW_W} y1={Y} y2={Y} />
                            );
                        })
                    }

                    {
                        series.map((s, index) => {

                            const VALUES = (s.values ?? []).map((v) => Number(v) || 0);

                            const POINTS = onBuildPoints(VALUES, {
                                width: VIEW_W, height: VIEW_H, max: MAX, padTop: PAD_TOP,
                            });

                            const PATH = onToPath(POINTS);

                            const AREA = `${PATH} L${POINTS[POINTS.length - 1].x},${VIEW_H} L${POINTS[0].x},${VIEW_H} Z`;

                            return (
                                <g key={s.label ?? index} data-tone={s.tone ?? 'accent'}>

                                    {showArea && <path className={`${BLOCK}__area`} d={AREA} />}

                                    <path className={`${BLOCK}__line`} d={PATH} vectorEffect="non-scaling-stroke" />

                                    {
                                        showDots && POINTS.map((p, i) => (
                                            <circle key={i} className={`${BLOCK}__dot`}
                                                cx={p.x} cy={p.y} r="3" vectorEffect="non-scaling-stroke" >
                                                <title>{`${labels[i] ?? i}: ${formatValue(VALUES[i])}`}</title>
                                            </circle>
                                        ))
                                    }

                                </g>
                            );
                        })
                    }

                </svg>

            </div>

            {
                labels.length > 0 && (
                    <div className={`${BLOCK}__labels`} aria-hidden="true">
                        {labels.map((label, i) => <span key={i} className={`${BLOCK}__label`}>{label}</span>)}
                    </div>
                )
            }

            <table className={`${BLOCK}__sr`}>
                <caption>{ariaLabel}</caption>
                <tbody>
                    {
                        series.map((s, i) => (
                            <tr key={s.label ?? i}>
                                <th scope="row">{s.label ?? `Serie ${i + 1}`}</th>
                                <td>{(s.values ?? []).map(formatValue).join(', ')}</td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>

        </div>
    );
};
